import React from 'react';
import { MapPin, FileText, Users, Calendar } from 'lucide-react';
import { DonutChart } from './DonutChart';
import { ProgressBar } from './ProgressBar';
import { BarChart } from './BarChart';
import { StatSection } from './StatSection';

interface DemographicStatsProps {
  zonaStats: Array<{ zona: string; count: number }>;
  documentoStats: {
    conDocumento: number;
    sinDocumento: number;
    porTipo: {
      cedula: number;
      tarjetaIdentidad: number;
      pasaporte: number;
      otro: number;
    };
  };
  registroPorMes: Array<{ mes: string; anio: number; count: number }>;
  totalMembers: number;
}

export const DemographicStats: React.FC<DemographicStatsProps> = ({
  zonaStats,
  documentoStats,
  registroPorMes,
  totalMembers
}) => {
  const { conDocumento, sinDocumento, porTipo } = documentoStats;

  // Preparar datos para el gráfico de barras de registro por mes
  const registroPorMesData = registroPorMes.map(item => ({
    label: `${item.mes}`,
    value: item.count,
    color: '#8b5cf6'
  }));

  return (
    <div className="space-y-6">
      {/* Zonas y Documentos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Zonas */}
        {zonaStats.length > 0 && (
          <StatSection title="Top Zonas / Barrios" icon={MapPin} iconColor="text-green-600">
            <div className="space-y-3">
              {zonaStats.map((zona, index) => (
                <ProgressBar
                  key={index}
                  label={zona.zona}
                  value={zona.count}
                  total={totalMembers}
                  color={index === 0 ? '#10b981' : index === 1 ? '#3b82f6' : index === 2 ? '#f59e0b' : '#8b5cf6'}
                />
              ))}
            </div>
          </StatSection>
        )}

        {/* Estadísticas de Documentos */}
        <div className="space-y-6">
          <StatSection title="Documentación" icon={FileText} iconColor="text-blue-600">
            <div className="flex flex-col items-center">
              <DonutChart
                data={[
                  { label: 'Con documento', value: conDocumento, color: '#10b981' },
                  { label: 'Sin documento', value: sinDocumento, color: '#ef4444' }
                ]}
                size={140}
                centerText={`${((conDocumento / Math.max(totalMembers, 1)) * 100).toFixed(0)}%`}
                centerSubtext="Con doc."
              />
              <div className="flex justify-center space-x-6 mt-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-xs text-gray-600">Con doc: {conDocumento}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                  <span className="text-xs text-gray-600">Sin doc: {sinDocumento}</span>
                </div>
              </div>
            </div>
          </StatSection>

          <StatSection title="Tipos de Documento" icon={Users} iconColor="text-indigo-600">
            <div className="space-y-3">
              <ProgressBar
                label="Cédula de Ciudadanía"
                value={porTipo.cedula}
                total={conDocumento || 1}
                color="#3b82f6"
              />
              <ProgressBar
                label="Tarjeta de Identidad"
                value={porTipo.tarjetaIdentidad}
                total={conDocumento || 1}
                color="#10b981"
              />
              <ProgressBar
                label="Pasaporte"
                value={porTipo.pasaporte}
                total={conDocumento || 1}
                color="#f59e0b"
              />
              <ProgressBar
                label="Otro"
                value={porTipo.otro}
                total={conDocumento || 1}
                color="#8b5cf6"
              />
            </div>
          </StatSection>
        </div>
      </div>

      {/* Registro por Mes */}
      {registroPorMesData.length > 0 && (
        <StatSection title="Miembros Registrados por Mes (Últimos 6 meses)" icon={Calendar} iconColor="text-orange-600">
          <div className="h-64">
            <BarChart data={registroPorMesData} />
          </div>
        </StatSection>
      )}
    </div>
  );
};

export default DemographicStats;
